import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { getServerUrl } from '../config';

export interface BiometricCredential {
  id: string;
  publicKey: string;
  counter: number;
  enabled: boolean;
  createdAt: string;
  lastUsed: string;
}

export interface BiometricChallenge {
  challenge: string;
  timeout: number;
}

export class BiometricService {
  private static instance: BiometricService;
  private serverUrl: string;

  private constructor() {
    this.serverUrl = getServerUrl();
  }

  public static getInstance(): BiometricService {
    if (!BiometricService.instance) {
      BiometricService.instance = new BiometricService();
    }
    return BiometricService.instance;
  }

  /**
   * Verifica si el navegador soporta WebAuthn
   */
  public isSupported(): boolean {
    return window.PublicKeyCredential !== undefined;
  }

  /**
   * Verifica si el usuario tiene credenciales biométricas habilitadas
   */
  public async hasBiometricCredentials(email: string): Promise<boolean> {
    try {
      if (!email || !email.trim()) {
        return false;
      }
      
      const response = await fetch(`${this.serverUrl}/api/biometric/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.hasCredentials === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking biometric credentials:', error);
      return false;
    }
  }

  /**
   * Registra una nueva credencial biométrica
   */
  public async registerBiometricCredential(email: string, password: string): Promise<boolean> {
    try {
      // Obtener opciones de registro del servidor
      const response = await fetch(`${this.serverUrl}/api/biometric/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error('Failed to get registration options');
      }

      const options = await response.json();

      // Verificar que las opciones tengan la estructura correcta
      if (!options || !options.publicKey) {
        throw new Error('Invalid registration options received from server');
      }
      
      // Iniciar registro en el navegador
      // La API devuelve { publicKey: {...} }, necesitamos extraer solo publicKey
      const credential = await startRegistration(options.publicKey);

      // Enviar credencial al servidor para verificación
      const verificationResponse = await fetch(`${this.serverUrl}/api/biometric/verify-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          credential,
        }),
      });

      if (verificationResponse.ok) {
        return true;
      } else {
        throw new Error('Registration verification failed');
      }
    } catch (error) {
      console.error('Error registering biometric credential:', error);
      throw error;
    }
  }

  /**
   * Autentica usando credenciales biométricas
   */
  public async authenticateWithBiometric(email: string): Promise<{ token: string; user: any }> {
    try {
      // Obtener opciones de autenticación del servidor
      const response = await fetch(`${this.serverUrl}/api/biometric/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to get authentication options');
      }

      const options = await response.json();

      // Iniciar autenticación en el navegador
      // Verificar que las opciones tengan la estructura correcta
      if (!options || !options.publicKey) {
        throw new Error('Invalid authentication options received from server');
      }
      
      const credential = await startAuthentication(options.publicKey);

      // Preparar el credential para el servidor (incluir rawId)
      const credentialForServer = {
        id: credential.id,
        rawId: credential.rawId,
        response: {
          authenticatorData: credential.response.authenticatorData,
          clientDataJSON: credential.response.clientDataJSON,
          signature: credential.response.signature
        },
        type: credential.type
      };

      // Enviar credencial al servidor para verificación
      const verificationResponse = await fetch(`${this.serverUrl}/api/biometric/verify-authentication`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          credential: credentialForServer,
        }),
      });

      if (verificationResponse.ok) {
        const data = await verificationResponse.json();
        return {
          token: data.token,
          user: data.user
        };
      } else {
        throw new Error('Authentication verification failed');
      }
    } catch (error) {
      console.error('Error authenticating with biometric:', error);
      throw error;
    }
  }

  /**
   * Deshabilita las credenciales biométricas del usuario
   */
  public async disableBiometricCredentials(email: string, password: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.serverUrl}/api/biometric/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error disabling biometric credentials:', error);
      return false;
    }
  }
}

export default BiometricService;
